import {
    BoxedRowList,
    ButtonPrimary,
    Callout,
    Divider,
    IconChevronLeftRegular,
    IconInformationUserRegular,
    IconLockClosedRegular,
    IconLockOpenRegular,
    IconWarningRegular,
    NavigationBar,
    skinVars,
    Stack,
    Tag,
    Text2,
    Text3,
    useDialog,
} from '@telefonica/mistica';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { getRoleLine } from '../../../utils/dataTransformation/productConsumption';
import CardWithSwitch from '../../../components/CartWithSwitch';
import './index.scss';
import { useConfigureLineContext } from '../../../context/configure-line-context/ConfigureLineContext';
import { useEffect, useState } from 'react';
import { getSIMSInfo, updateRestriction } from '../../../services/ConfigureLineService';
import { itsMoreThanXminutes } from '../../../utils/dataTransformation/dateConversions';
import { AxiosError } from 'axios';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import { useQuery } from '@tanstack/react-query';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';
import useConfigureLine from '../utils/hooks';

const BlockedLine = () => {
    const navigate = useNavigate();
    const { dialog } = useDialog();
    const {
        setBlockedLineSuccess,
        blocked,
        lineActive,
        blockedLineRestrictionId,
        isConvergent,
        setConfigureLineError,
    } = useConfigureLineContext();
    const { checkRestrictions } = useConfigureLine();
    const [isBlocked, setIsBlocked] = useState(false);
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const { data: simInfo } = useQuery({
        queryKey: ['sims-info', lineActive],
        queryFn: () => getSIMSInfo(lineActive),
        enabled: !!lineActive,
    });
    const navigationBackHandler = () => navigate(`/configure-line`);

    useEffect(() => {
        checkRestrictions(lineActive, setIsBlocked);
    }, []);

    const saveHandler = async () => {
        if (localStorage.getItem('timestamp')) {
            const timeInterval: any = JSON.parse(localStorage.getItem('timestamp')!);

            if (!itsMoreThanXminutes(timeInterval.time, 4)) {
                dialog({
                    asset: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
                    title: t('requestNotAvaiable'),
                    message: (() => {
                        return (
                            <Stack space={16}>
                                <Text3 light color={skinVars.colors.borderHigh}>
                                    {t('notPosibleFinishedRequest')}
                                </Text3>
                                <Text3 light color={skinVars.colors.borderHigh}>
                                    {t('call1551')}
                                </Text3>
                            </Stack>
                        );
                    })() as any,
                    onAccept() {},
                    acceptText: t('buttonUnderstoodFeedback'),
                });
                return;
            }
        }

        try {
            await updateRestriction({
                productId: simInfo?.productId,
                restrictionSpecId: blockedLineRestrictionId,
                activate: isBlocked,
            });

            localStorage.setItem('timestamp', JSON.stringify({ id: 'blockLine', time: `${new Date()}` }));

            setBlockedLineSuccess(true);
        } catch (error) {
            setBlockedLineSuccess(false);
            setConfigureLineError(error as AxiosError);
        } finally {
            navigate('/configure-line/block-line/feedback');
        }
    };

    return (
        <div className='blocked-line'>
            <div className='blocked-line__header'>
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <>
                        <div className='blocked-line__header-navigationBar'>
                            <NavigationBar
                                withBorder={false}
                                onBack={navigationBackHandler}
                                title={blocked ? t('unlockLineHeader') : t('blockLineHeader')}
                            />
                        </div>
                        <div className='blocked-line__header-return' onClick={navigationBackHandler}>
                            <div className='blocked-line__header-return-icon'>
                                <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                            </div>
                            <Text2 medium color={skinVars.colors.textPrimary}>
                                {t('returnResponsive')}
                            </Text2>
                        </div>
                    </>
                )}
            </div>
            <div className='blocked-line__breadcrumbs'>
                <BreadCrumbsWrapper
                    title={!blocked ? t('blockLineText1') : t('unlockLineHeader')}
                    breadcrumbs={
                        !isConvergent
                            ? [
                                  { title: t('breadcrumbsHome'), url: '/home' },
                                  {
                                      title: t('lineManagement'),
                                      url: `/configure-line`,
                                  },
                              ]
                            : [
                                  { title: t('breadcrumbsHome'), url: '/home' },
                                  {
                                      title: getRoleLine(JSON.parse(consumptions), simInfo?.productPsId!),
                                      url: `/mobile?productPsId=${simInfo?.productPsId}`,
                                  },
                                  {
                                      title: t('lineManagement'),
                                      url: `/configure-line`,
                                  },
                              ]
                    }
                />
            </div>

            <Divider />

            <div className='blocked-line__body'>
                <div className={`blocked-line__switch-row${isBlocked ? ' blocked-line__switch-row--checked' : ''}`}>
                    <BoxedRowList>
                        <CardWithSwitch
                            title={t('blockLineText1')}
                            subtitle={formatMobileNumber(simInfo?.number)}
                            state={isBlocked}
                            onHandleSwicth={() => setIsBlocked((prevState) => !prevState)}
                            index={''}
                            boxed
                            headline={
                                <Tag type={isBlocked ? 'success' : 'inactive'}>
                                    {isBlocked ? t('activated') : t('disabled')}
                                </Tag>
                            }
                            icon={
                                !isBlocked ? (
                                    <IconLockOpenRegular color={skinVars.colors.brand} size={24} />
                                ) : (
                                    <IconLockClosedRegular color={skinVars.colors.brand} size={24} />
                                )
                            }
                        />
                    </BoxedRowList>
                </div>

                <Callout
                    title={blocked ? t('blockLineCaptionTitle2') : t('blockLineCaptionTitle1')}
                    description={blocked ? t('blockLineCaptionDescription2') : t('blockLineCaptionDescription1')}
                    asset={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                />
            </div>

            <div className='blocked-line__bottom'>
                <ButtonPrimary
                    disabled={blocked === isBlocked}
                    role='onPress'
                    onPress={saveHandler}
                    StartIcon={() => <IconLockClosedRegular color={skinVars.colors.background} size={24} />}
                >
                    {blocked ? t('confirmUnlock') : t('confirmLock')}
                </ButtonPrimary>
            </div>
        </div>
    );
};

export default BlockedLine;
